import React, { useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { Checkbox } from "../../../../shared/Checkbox/Checkbox";
import styles from "../scss/TimeCategory.module.scss";
import { DeleteIcon } from "../../../../assets/Icons/DeleteIcon";
import { Button } from "../../../../shared/Button/Button";
import { PlusIcon } from "../../../../assets/Icons/PlusIcon";
import { Input } from "../../../../shared/Input/Input";
import { Modal } from "../../../../shared/Modal/Modal";
import { uiNotification } from "../../../../services/UINotificationService";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import { useCreateTimeTrackingConfig } from "../hooks/useCreateTimeTrackingConfig";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../../consts/api";
import { useDeleteTimeTrackingConfig } from "../hooks/useDeleteTimeTrackingConfig";
import { useToggleTimeTrackingConfig } from "../hooks/useToggleTimeTrackingConfig";

const TimeCategory = ({
  id,
  name,
  isAvailable,
  isDefault,
  isLastRow,
  totalTrackedMinutes,
  canEditTimeTrackingConfigs,
}) => {
  const [addNewCategory, setAddNewCategory] = useState(false);
  const [deleteModalId, setDeleteModalId] = useState(null);
  const queryClient = useQueryClient();

  const { mutate: toggleConfig, isLoading: isToggling } =
    useToggleTimeTrackingConfig(id);

  const { mutate: deleteCategory, isLoading: isDeletingCategory } =
    useDeleteTimeTrackingConfig(deleteModalId, {
      onSuccess: () => {
        setDeleteModalId(null);
        queryClient.invalidateQueries([
          QUERY_KEYS.timeclockSettings.getTimeTrackingConfigs,
        ]);
        uiNotification.success("Category deleted successfully!");
      },
      onError: (error) => {
        uiNotification.error(error.response.data.message);
      },
    });

  const { mutate: createCategory, isLoading: isCreatingCategory } =
    useCreateTimeTrackingConfig();

  const { values, setFieldValue, errors, handleSubmit } = useFormik({
    initialValues: {
      newCategoryName: "",
      validationSchema: yup.object().shape({
        newCategoryName: yup.string().required(),
      }),
    },
    onSubmit: (values) => {
      createCategory(
        {
          tracked_as: values.newCategoryName.replace(" ", "_").toLowerCase(),
          display_name: values.newCategoryName,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([
              QUERY_KEYS.timeclockSettings.getTimeTrackingConfigs,
            ]);
            setAddNewCategory(false);
          },
          onError: (error) => {
            uiNotification.error(error.response.data.message);
          },
        },
      );
    },
  });

  const toggleAvailableForm = useFormik({
    initialValues: {
      isAvailable,
    },
    onSubmit: (values) =>
      toggleConfig(
        { is_available: !values.isAvailable },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([
              QUERY_KEYS.timeclockSettings.getTimeTrackingConfigs,
            ]);
            toggleAvailableForm.setFieldValue("isAvailable", !isAvailable);
            uiNotification.success("Category updated successfully!");
          },
          onError: (error) => {
            uiNotification.error(error.response.data.message);
          },
        },
      ),
  });

  const handleCancel = () => {
    setFieldValue("newCategoryName", "");
    setAddNewCategory(false);
  };

  return (
    <>
      <tr>
        <td className={styles.newCategoryInputTd}>{name}</td>
        <td className={styles.actionsTd}>
          <div className={styles.checkboxContainer}>
            {isToggling ? (
              <CircularProgress size="small" />
            ) : (
              <Checkbox
                isChecked={toggleAvailableForm.values.isAvailable}
                labelClassName={styles.actionsTdCheckboxLabel}
                onChange={toggleAvailableForm.handleSubmit}
                isLoading={isToggling}
                checkboxId={`toggle-available-${id}`}
                isDisabled={isDefault || !canEditTimeTrackingConfigs.permitted}
              />
            )}
            <label htmlFor={`toggle-available-${id}`}>
              Count in RPH Report
            </label>
          </div>

          {isDefault ? (
            <span className={styles.defaultText}>Default</span>
          ) : (
            canEditTimeTrackingConfigs.permitted && (
              <Button
                className={styles.iconButtons}
                variant="text"
                onClick={() => setDeleteModalId(id)}
              >
                <DeleteIcon width="18px" />
              </Button>
            )
          )}
          {isLastRow &&
            !addNewCategory &&
            canEditTimeTrackingConfigs.permitted && (
              <Button
                className={styles.iconButtons}
                variant="text"
                onClick={() => setAddNewCategory(true)}
              >
                <PlusIcon width="18px" />
              </Button>
            )}
        </td>
      </tr>
      {addNewCategory && (
        <tr>
          <td className={styles.newCategoryInputTd}>
            <Input
              size="small"
              name="newCategoryName"
              placeholder="Enter Category Name"
              className={styles.newCategoryInput}
              value={values.newCategoryName}
              onChange={(event) =>
                setFieldValue("newCategoryName", event.target.value)
              }
              isError={Boolean(errors.newCategoryName)}
            />
          </td>
          <td>
            <div className={styles.newCategoryActionButtons}>
              <Button
                size="tiny"
                onClick={handleSubmit}
                leftAdornment={
                  isCreatingCategory ? (
                    <CircularProgress size="small" color="white" />
                  ) : null
                }
                isDisabled={isCreatingCategory}
              >
                Save
              </Button>
              <Button size="tiny" variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </td>
        </tr>
      )}
      {deleteModalId && (
        <Modal
          isOpen
          onClose={() => setDeleteModalId(null)}
          header={<h2>Delete Category</h2>}
          headerNoBorder
          footerNoBorder
          footer={
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDeleteModalId(null)}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={deleteCategory}
                leftAdornment={
                  isDeletingCategory ? (
                    <CircularProgress size="tiny" color="white" />
                  ) : null
                }
                isDisabled={isDeletingCategory}
              >
                Delete
              </Button>
            </>
          }
          footerClassName={styles.deleteModalFooter}
        >
          <p>Are you sure you want to delete this category?</p>
          {totalTrackedMinutes > 0 && (
            <p>
              This category has {totalTrackedMinutes / 60} hours assigned to it.
              These hours will be automatically moved to the default category:
              Provider Time.
            </p>
          )}
        </Modal>
      )}
    </>
  );
};

export default TimeCategory;
